
import React from 'react';

const DashboardOverview: React.FC = () => {
  const stats = [
    {
      title: '总项目数',
      value: '156',
      change: '+12%',
      changeType: 'positive',
      icon: 'ri-folder-line',
      color: 'blue'
    },
    {
      title: '活跃客户',
      value: '89',
      change: '+8%',
      changeType: 'positive',
      icon: 'ri-user-line',
      color: 'green'
    },
    {
      title: '本月收入',
      value: '¥285,000',
      change: '+15%',
      changeType: 'positive',
      icon: 'ri-money-dollar-circle-line',
      color: 'purple'
    },
    {
      title: '进行中项目',
      value: '23',
      change: '-3%',
      changeType: 'negative',
      icon: 'ri-time-line',
      color: 'orange'
    }
  ];

  const recentProjects = [
    {
      id: 1,
      name: 'NFT数字藏品平台',
      client: '艺术科技公司',
      status: '进行中',
      progress: 75,
      deadline: '2024-02-15'
    },
    {
      id: 2,
      name: 'AI智能后台管理系统',
      client: '数据分析公司',
      status: '测试中',
      progress: 90,
      deadline: '2024-01-30'
    },
    {
      id: 3,
      name: '电商小程序开发',
      client: '零售连锁企业',
      status: '开发中',
      progress: 60,
      deadline: '2024-03-01'
    },
    {
      id: 4,
      name: 'PHP到Go迁移',
      client: '金融科技公司',
      status: '规划中',
      progress: 25,
      deadline: '2024-04-15'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case '进行中':
        return 'bg-blue-100 text-blue-800';
      case '测试中':
        return 'bg-yellow-100 text-yellow-800';
      case '开发中':
        return 'bg-green-100 text-green-800';
      case '规划中':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">数据概览</h1>
        <p className="text-gray-600 mt-1">实时监控业务数据和项目进展</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                <div className="flex items-center mt-2">
                  <span className={`text-sm font-medium ${
                    stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {stat.change}
                  </span>
                  <span className="text-sm text-gray-500 ml-1">较上月</span>
                </div>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center bg-${stat.color}-100`}>
                <i className={`${stat.icon} text-xl text-${stat.color}-600`}></i>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Projects */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">最近项目</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  项目名称
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  客户
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  状态
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  进度
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  截止日期
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentProjects.map((project) => (
                <tr key={project.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{project.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{project.client}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(project.status)}`}>
                      {project.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${project.progress}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-900">{project.progress}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {project.deadline}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-add-line text-xl text-blue-600"></i>
            </div>
            <div className="ml-4">
              <h4 className="text-lg font-semibold text-gray-900">新建项目</h4>
              <p className="text-sm text-gray-600">创建新的项目并分配团队</p>
            </div>
          </div>
          <button className="w-full mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap">
            立即创建
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-user-add-line text-xl text-green-600"></i>
            </div>
            <div className="ml-4">
              <h4 className="text-lg font-semibold text-gray-900">添加客户</h4>
              <p className="text-sm text-gray-600">录入新客户信息</p>
            </div>
          </div>
          <button className="w-full mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap">
            添加客户
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <i className="ri-bar-chart-line text-xl text-purple-600"></i>
            </div>
            <div className="ml-4">
              <h4 className="text-lg font-semibold text-gray-900">AI报表</h4>
              <p className="text-sm text-gray-600">生成智能数据分析</p>
            </div>
          </div>
          <button className="w-full mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors cursor-pointer whitespace-nowrap">
            生成报表
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
